import { useTranslation } from 'react-i18next';
import { Check, Hourglass, Swords, UserCheck, UserPlus, UserRound, Users } from 'lucide-react';

import { cn } from '@/lib/cn';
import type { Gesto, Icona, ParolaIcona, StatoSocial } from '@/lib/qgammon/storicoPartite';
import { iconeDi } from '@/lib/qgammon/storicoPartite';
import { useStatoAvversari } from '@/components/hub/panels/storico/contestoStato';

/**
 * Le icone accanto al nome dell'avversario: profilo · segui · amicizia · sfida (tavola a2, L-2604).
 * Solo icone, niente testo nei bottoni: il nome accessibile e' la parola della legenda + il nome dell'avversario.
 *
 * ⚠️ «Aggiungi» e' `Users` e NON `UserPlus`: nella TMA `UserPlus` vuol gia' dire «Segui» in tutti e tre i componenti
 * social, e in una riga di sole icone due `UserPlus` non si distinguerebbero.
 */

/** La chiave di `general` (o di `backgammon`, col prefisso `backgammon:`) che da' il nome di ogni parola della legenda. */
const CHIAVE: Record<ParolaIcona, string> = {
  profile: 'social.dm.riga.profilo',
  follow: 'social.follow',
  following: 'social.following',
  addFriend: 'social.add_friend',
  requestPending: 'social.request_pending',
  accept: 'social.accept',
  friends: 'backgammon:storico.amici',
  challenge: 'social.challenge',
};

function useNomeParola() {
  const { t } = useTranslation(['general', 'backgammon']);
  return (p: ParolaIcona): string => t(CHIAVE[p]);
}

/** Il disegno di una icona. Senza stato: serve identico al bottone della riga e alla legenda. */
export function GlifoIcona({ icona }: { icona: Pick<Icona, 'parola'> }) {
  const c = 'h-3.5 w-3.5';
  switch (icona.parola) {
    case 'profile':
      return <UserRound aria-hidden className={c} />;
    case 'follow':
      return <UserPlus aria-hidden className={c} />;
    case 'following':
      return <UserCheck aria-hidden className={c} />;
    case 'addFriend':
      return <Users aria-hidden className={c} />;
    case 'requestPending':
      return <Hourglass aria-hidden className={c} />;
    case 'accept':
      return <Check aria-hidden className={c} />;
    case 'friends':
      // `Users` accesa con un bollino ✓: la forma NUOVA dell'amicizia accettata.
      return (
        <span className="relative inline-grid place-items-center">
          <Users aria-hidden className={c} />
          <span className="absolute -bottom-1 -right-1 grid h-2.5 w-2.5 place-items-center rounded-full bg-brand text-brand-fg">
            <Check aria-hidden className="h-2 w-2" strokeWidth={4} />
          </span>
        </span>
      );
    case 'challenge':
      return <Swords aria-hidden className={c} />;
  }
}

function coloreIcona(i: Pick<Icona, 'accesa' | 'piena'>): string {
  // «Spenta» = fondo secondario; «accesa» = brand al 15%; «brand pieno» = solo «Accetta».
  if (i.piena) return 'bg-brand text-brand-fg';
  if (i.accesa) return 'bg-brand/15 text-brand';
  return 'bg-bg-tertiary text-fg-muted';
}

export function IconeAvversario({
  sub,
  nome,
  dalLotto,
}: {
  sub: string;
  nome: string;
  /** Lo stato letto dal lotto di QSocials, o `null` (lotto assente, fallito, sub omesso da un server precedente alla #29): nessuna icona. */
  dalLotto: StatoSocial | null;
}) {
  const stato = useStatoAvversari();
  const nomeDi = useNomeParola();
  const corrente = stato.statoDi(sub, dalLotto);
  const icone = iconeDi(corrente, stato.sfidaAccesa);
  if (icone.length === 0) return null;
  const occupato = stato.inVolo(sub);
  return (
    <span className="flex shrink-0 items-center gap-1.5" data-testid="storico-icone">
      {icone.map((i) => (
        <button
          key={i.posto}
          type="button"
          data-gesto={i.gesto}
          data-posto={i.posto}
          aria-label={`${nomeDi(i.parola)} ${nome}`}
          aria-busy={occupato || undefined}
          // Un gesto in volo ferma le icone di TUTTE le righe di questo avversario (non il profilo, che non scrive).
          disabled={occupato && i.gesto !== 'profilo' && i.gesto !== 'sfida'}
          onClick={() => stato.esegui(sub, nome, i.gesto as Gesto, dalLotto)}
          className={cn(
            'grid h-7 w-7 shrink-0 place-items-center rounded-full transition-opacity active:opacity-70 disabled:opacity-50',
            coloreIcona(i),
          )}
        >
          <GlifoIcona icona={i} />
        </button>
      ))}
    </span>
  );
}

/**
 * La legenda «Icone accanto al nome», in fondo alla lista (come nella tavola): spiega le icone e dice che la sfida
 * e' fuori classifica — l'unico posto dove quella frase sta, perche' il foglio della sfida e' di un altro cantiere.
 * La riga «Sfida» c'e' solo con la serratura aperta: una legenda di un'icona che non puo' comparire e' rumore.
 */
export function LegendaIcone({ conSfida }: { conSfida: boolean }) {
  const { t } = useTranslation('backgammon');
  const nomeDi = useNomeParola();
  const voci: Array<{ parola: ParolaIcona; accesa: boolean; piena: boolean; testo: string }> = [
    { parola: 'profile', accesa: false, piena: false, testo: t('storico.legenda.profilo') },
    { parola: 'follow', accesa: false, piena: false, testo: t('storico.legenda.segui') },
    { parola: 'following', accesa: true, piena: false, testo: t('storico.legenda.seguiGia') },
    { parola: 'addFriend', accesa: false, piena: false, testo: t('storico.legenda.aggiungi') },
    { parola: 'requestPending', accesa: false, piena: false, testo: t('storico.legenda.inSospeso') },
    { parola: 'accept', accesa: false, piena: true, testo: t('storico.legenda.accetta') },
    { parola: 'friends', accesa: true, piena: false, testo: t('storico.legenda.amici') },
    ...(conSfida ? [{ parola: 'challenge' as const, accesa: false, piena: false, testo: t('storico.legenda.sfida') }] : []),
  ];
  return (
    <section data-testid="storico-legenda" className="rounded-2xl bg-bg-secondary p-3">
      <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-fg-muted">{t('storico.legenda.titolo')}</h3>
      <ul className="flex flex-col gap-1.5">
        {voci.map((v) => (
          <li key={v.parola} className="flex items-start gap-2.5 text-xs text-fg-muted">
            <span aria-hidden className={cn('grid h-7 w-7 shrink-0 place-items-center rounded-full', coloreIcona(v))}>
              <GlifoIcona icona={v} />
            </span>
            <span className="pt-1">
              <span className="font-bold text-fg">{nomeDi(v.parola)}</span> — {v.testo}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
